import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowLeft, FileText, ChevronDown, ChevronUp, Shield } from 'lucide-react';

const TermsOfUse = () => {
    const navigate = useNavigate();
    const [openSections, setOpenSections] = useState({});

    const toggleSection = (index) => {
        setOpenSections(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const sections = [
        {
            title: "1. Objeto e Aceitação",
            content: `Estes Termos de Uso ("Termos") regem o acesso e utilização da plataforma **Simby** ("Plataforma"), de titularidade da **Paula Pontvianne Consultoria em Comunicação Empática** ("Empresa"), inscrita no CNPJ correspondente.

Ao criar uma conta ou utilizar qualquer funcionalidade da Plataforma, você ("Usuário") declara ter lido, compreendido e concordado integralmente com estes Termos e com a nossa **Política de Privacidade**.

Se você não concordar com qualquer disposição, não utilize a Plataforma.`
        },
        {
            title: "2. Cadastro e Conta",
            content: `Para utilizar a Plataforma, é necessário criar uma conta por meio de login social (Google). Ao fazê-lo, você declara que:

• As informações fornecidas são verdadeiras, precisas e atualizadas
• Você é maior de 18 anos ou possui autorização de responsável legal
• Você é responsável pela confidencialidade das suas credenciais de acesso
• Qualquer atividade realizada com sua conta é de sua responsabilidade

A Empresa reserva-se o direito de encerrar contas que violem estes Termos, sem aviso prévio.`
        },
        {
            title: "3. Licença de Uso",
            content: `A Empresa concede ao Usuário uma licença **limitada, não exclusiva, intransferível e revogável** para acessar e utilizar a Plataforma exclusivamente para fins pessoais e não comerciais.

É expressamente vedado:
• Copiar, modificar, distribuir ou criar obras derivadas do conteúdo da Plataforma
• Utilizar a Plataforma para fins comerciais sem autorização prévia por escrito
• Realizar engenharia reversa ou extrair código-fonte
• Revender, sublicenciar ou ceder o acesso a terceiros`
        },
        {
            title: "4. Conteúdo do Usuário",
            content: `Ao inserir conteúdo na Plataforma (incluindo registros no Diário Emocional, respostas em simuladores e quizzes), você:

• Mantém a titularidade dos seus dados pessoais e sensíveis
• Concede à Empresa uma licença limitada para processar esses dados exclusivamente com o objetivo de fornecer a experiência de aprendizado personalizada
• Reconhece que esse conteúdo **não será utilizado para treinar modelos de Inteligência Artificial**
• Autoriza a geração de relatórios **anonimizados e agregados** para fins analíticos internos e, no caso de contratos empresariais, para o gestor da conta

Para mais detalhes sobre como tratamos seus dados, consulte nossa **Política de Privacidade**.`
        },
        {
            title: "5. Dados Pessoais e Privacidade",
            content: `O tratamento de dados pessoais e sensíveis na Plataforma é regido pela nossa **Política de Privacidade**, disponível em /privacy, e em conformidade com a **Lei Geral de Proteção de Dados (LGPD — Lei 13.709/2018)**.

Você possui os seguintes direitos em relação aos seus dados:
• **Acesso**: Solicitar cópia de todos os dados armazenados
• **Portabilidade**: Exportar seus dados em formato estruturado (JSON)
• **Correção**: Atualizar dados incompletos ou incorretos
• **Eliminação**: Solicitar a exclusão imediata da sua conta e todos os dados pessoais associados
• **Revogação do consentimento**: Retirar o consentimento a qualquer momento

Para exercer seus direitos, acesse a seção "Privacidade e Dados" no seu perfil ou contate nosso DPO: **dpo@paulapontvianne.com.br**`
        },
        {
            title: "6. Pagamentos e Assinatura",
            content: `O acesso completo à Plataforma requer uma assinatura paga. As condições são:

**Cobrança**:
• Os planos e valores vigentes estão descritos na página de assinatura
• A cobrança é processada pelo **Stripe**, plataforma certificada PCI-DSS
• Não armazenamos dados completos de cartão de crédito

**Cancelamento**:
• O Usuário pode cancelar sua assinatura a qualquer momento pelo perfil ou pelo suporte
• O cancelamento interrompe a renovação automática; o acesso é mantido até o fim do período pago

**Reembolso**:
• Solicitações de reembolso devem ser feitas em até **7 dias corridos** após a cobrança, conforme o Código de Defesa do Consumidor (Lei 8.078/90)
• Reembolsos fora deste prazo são analisados caso a caso

**Dados fiscais**: Dados de transações financeiras podem ser retidos pelo prazo exigido pela legislação fiscal brasileira, mesmo após a exclusão da conta.`
        },
        {
            title: "7. Obrigações e Condutas Proibidas",
            content: `O Usuário compromete-se a utilizar a Plataforma de forma lícita e respeitosa. São condutas proibidas:

• Usar a Plataforma para qualquer finalidade ilegal ou não autorizada
• Violar direitos de propriedade intelectual da Empresa ou de terceiros
• Transmitir conteúdo ofensivo, difamatório, discriminatório ou prejudicial
• Tentar comprometer a segurança, integridade ou disponibilidade da Plataforma
• Criar múltiplas contas para contornar restrições ou limitações
• Utilizar bots, scrapers ou outros meios automatizados para acessar a Plataforma

A violação destas obrigações pode resultar na suspensão ou encerramento imediato da conta.`
        },
        {
            title: "8. Responsabilidade e Garantias",
            content: `**Limitação de responsabilidade**:
A Plataforma é fornecida "como está" e "conforme disponível". Na máxima extensão permitida por lei, a Empresa não garante que a Plataforma será ininterrupta, livre de erros ou adequada para uma finalidade específica.

**Conteúdo educacional**:
O conteúdo da Plataforma tem finalidade educacional em inteligência emocional e comunicação não violenta. Não substitui acompanhamento psicológico, psiquiátrico ou médico profissional.

**Responsabilidade máxima**:
Em nenhuma hipótese a responsabilidade total da Empresa excederá o valor pago pelo Usuário nos 12 meses anteriores ao evento gerador da responsabilidade.`
        },
        {
            title: "9. Rescisão",
            content: `A Empresa pode suspender ou encerrar o acesso do Usuário à Plataforma, com ou sem aviso prévio, nos seguintes casos:

• Violação de qualquer disposição destes Termos
• Inadimplência no pagamento da assinatura
• Conduta que prejudique outros usuários ou a Empresa
• Exigência legal ou ordem judicial

O Usuário pode encerrar sua conta a qualquer momento, exercendo o direito de eliminação de dados previsto na LGPD, conforme descrito na seção 5 destes Termos.`
        },
        {
            title: "10. Disposições Gerais",
            content: `**Lei aplicável**: Estes Termos são regidos pelas leis da República Federativa do Brasil, em especial pela LGPD (Lei 13.709/2018), pelo Código de Defesa do Consumidor (Lei 8.078/1990) e pelo Marco Civil da Internet (Lei 12.965/2014).

**Foro**: Fica eleito o foro da Comarca de São Paulo/SP para dirimir eventuais controvérsias decorrentes destes Termos, com renúncia a qualquer outro, por mais privilegiado que seja.

**Atualização**: Estes Termos podem ser atualizados periodicamente. Mudanças significativas serão comunicadas por e-mail ou notificação na Plataforma. O uso continuado após a notificação implica aceitação dos novos termos.

**Contato**: Paula Pontvianne Consultoria em Comunicação Empática | DPO: Aislan Munin Pavanello | dpo@paulapontvianne.com.br

**Data de vigência**: 15 de abril de 2026`
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-950 to-slate-900">
            {/* Background effects */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute top-20 left-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-20 right-10 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl" />
            </div>

            {/* Header */}
            <header className="relative z-50 py-6 border-b border-white/10">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between">
                        <Button
                            variant="ghost"
                            className="text-white/80 hover:text-white hover:bg-white/10"
                            onClick={() => navigate(-1)}
                        >
                            <ArrowLeft className="w-5 h-5 mr-2" />
                            Voltar
                        </Button>
                        <img src="/logo-simby.png" alt="Simby" className="h-10" />
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className="relative py-8">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Title */}
                    <div className="mb-8 text-center">
                        <div className="flex items-center gap-3 mb-4 justify-center">
                            <FileText className="w-8 h-8 text-purple-400" />
                            <h1 className="text-3xl md:text-4xl font-bold text-white">
                                Termos de Uso
                            </h1>
                        </div>
                        <p className="text-lg text-white/60">
                            Condições gerais para utilização da plataforma Simby
                        </p>
                        <p className="text-sm mt-2 text-white/40">
                            Vigência: 15 de abril de 2026
                        </p>
                    </div>

                    {/* Intro Card */}
                    <Card className="mb-8 p-6 bg-white/5 border-white/10 backdrop-blur-sm">
                        <div className="flex items-start gap-3">
                            <Shield className="w-6 h-6 text-purple-400 mt-1 flex-shrink-0" />
                            <div>
                                <h3 className="text-lg font-bold text-white mb-2">
                                    Leia com atenção antes de utilizar a Plataforma
                                </h3>
                                <p className="text-white/70 leading-relaxed">
                                    Estes Termos constituem um contrato legal entre você e a <strong className="text-white">Paula Pontvianne Consultoria em Comunicação Empática</strong>.
                                    Ao criar uma conta ou usar o Simby, você confirma que leu e aceita estes Termos em sua totalidade.
                                </p>
                                <p className="text-white/60 mt-3 text-sm">
                                    Para dúvidas, entre em contato com nosso DPO: <a href="mailto:dpo@paulapontvianne.com.br" className="text-purple-400 hover:underline">dpo@paulapontvianne.com.br</a>
                                </p>
                            </div>
                        </div>
                    </Card>

                    {/* Accordion Sections */}
                    <div className="space-y-3">
                        {sections.map((section, index) => (
                            <Card
                                key={index}
                                className="overflow-hidden transition-all duration-200 bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10"
                            >
                                <button
                                    onClick={() => toggleSection(index)}
                                    className="w-full p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                                >
                                    <span className="font-semibold text-white pr-4">
                                        {section.title}
                                    </span>
                                    {openSections[index] ? (
                                        <ChevronUp className="w-5 h-5 text-white/50 flex-shrink-0" />
                                    ) : (
                                        <ChevronDown className="w-5 h-5 text-white/50 flex-shrink-0" />
                                    )}
                                </button>

                                {openSections[index] && (
                                    <div className="px-5 pb-5 pt-0 text-white/70">
                                        <div className="pt-4 border-t border-white/10">
                                            <div
                                                className="whitespace-pre-line leading-relaxed"
                                                dangerouslySetInnerHTML={{
                                                    __html: section.content.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </Card>
                        ))}
                    </div>

                    {/* Footer links */}
                    <Card className="mt-8 p-6 bg-gradient-to-r from-purple-600/20 to-indigo-600/20 border-white/10 backdrop-blur-xl">
                        <p className="text-white/70 text-sm text-center mb-4">
                            Ao utilizar o Simby, você também concorda com nossa{' '}
                            <a href="/privacy" className="text-purple-400 hover:underline">Política de Privacidade</a>.
                        </p>
                        <div className="flex justify-center">
                            <Button
                                size="lg"
                                className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white px-8 py-6 text-lg rounded-2xl shadow-xl"
                                onClick={() => navigate('/')}
                            >
                                Voltar para a Home
                            </Button>
                        </div>
                    </Card>

                    <div className="mt-8 pb-8 text-center">
                        <p className="text-white/30 text-xs">
                            © 2026 Paula Pontvianne Consultoria em Comunicação Empática. Todos os direitos reservados.
                            <span className="ml-2 text-white/20">v4.0</span>
                        </p>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default TermsOfUse;
